// Pure helpers for editing an AI form definition (no React).
// Ported from CCP-V2 admin-ui features/forms/definition.ts; the definition
// JSON is V2's, except case_types[].area_id holds the V1 area id as a string.

import { I } from '../../../common/v5/config';

export const TAB_TYPES = "types"
	, TAB_FIELDS = "fields"
	, TAB_AI = "ai"
	, TAB_DESIGN = "design"
	, TAB_EMBED = "embed"
	;

export const LANGUAGE_NAMES = {
	sv: "Svenska",
	en: "English",
	no: "Norsk",
	da: "Dansk",
	fi: "Suomi",
	de: "Deutsch",
};

export const FIELD_TYPES = ["text", "textarea", "email", "phone", "number", "select", "checkbox", "date", "file"];

export const FIELD_TYPE_NAMES = {
	text: I("Short text"),
	textarea: I("Long text"),
	email: I("Email"),
	phone: I("Phone"),
	number: I("Number"),
	select: I("Choice list"),
	checkbox: I("Checkbox"),
	date: I("Date"),
	file: I("Attachments"),
};

const RESERVED = ["description", "name", "email", "website"];

// A key the service accepts ([a-z0-9_], <= 40), unique among `taken`.
export function makeKey(label, taken) {
	const base = (label || "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.replace(/ø/g, "o")
		.replace(/æ/g, "ae")
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 34) || "item";
	const used = new Set([...(taken || []), ...RESERVED]);
	if (!used.has(base)) {
		return base;
	}
	for (let n = 2; ; n++) {
		const candidate = base + "_" + n;
		if (!used.has(candidate)) {
			return candidate;
		}
	}
}

// What the key inputs accept while typing.
export function cleanKey(value) {
	return (value || "").toLowerCase().replace(/[^a-z0-9_]/g, "_").slice(0, 40);
}

export function moveItem(list, index, delta) {
	const target = index + delta;
	if (target < 0 || target >= list.length) {
		return list;
	}
	const next = list.slice();
	const tmp = next[index];
	next[index] = next[target];
	next[target] = tmp;
	return next;
}

export function textIn(text, lang, fallback) {
	if (!text) {
		return "";
	}
	if (text[lang] != null) {
		return text[lang];
	}
	if (text[fallback] != null) {
		return text[fallback];
	}
	const values = Object.values(text);
	return values.length ? values[0] : "";
}

// The text with `lang` set; an empty value removes that language.
export function setText(text, lang, value) {
	const next = Object.assign({}, text || {});
	if (value) {
		next[lang] = value;
	} else {
		delete next[lang];
	}
	return next;
}

const DEFAULT_THEME = {
	primary_color: "#6d28d9",
	button_text_color: null,
	text_color: null,
	background: null,
	field_background: null,
	field_border: null,
	page_background: null,
	font: "system",
	font_family: null,
	font_size: "normal",
	radius: 12,
	style: "flat",
	spacing: "normal",
	max_width: null,
	hide_header: false,
	button_align: "right",
	button_label: {},
	custom_css: "",
	logo_url: null,
	avatars: [],
};

const DEFAULT_BUILTIN = { label: {}, placeholder: {}, required: true };

export const ATTACHMENTS_DEFAULT = { enabled: true, label: {}, help: {}, required: false };

function obj(v) {
	return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}

function arr(v) {
	return Array.isArray(v) ? v : [];
}

// Fill in what the service leaves out, so the editor never meets undefined.
export function normalize(defn) {
	const d = obj(defn);
	const languages = arr(d.languages).length ? d.languages : ["sv"];
	const default_language = languages.indexOf(d.default_language) >= 0 ? d.default_language : languages[0];
	const theme = Object.assign({}, DEFAULT_THEME, obj(d.theme));
	theme.avatars = arr(theme.avatars);
	theme.button_label = obj(theme.button_label);
	if (theme.custom_css == null) {
		theme.custom_css = "";
	}
	const builtin = (b, required) => {
		const x = Object.assign({}, DEFAULT_BUILTIN, { required }, obj(b));
		x.label = obj(x.label);
		x.placeholder = obj(x.placeholder);
		return x;
	};
	return Object.assign({}, d, {
		languages,
		default_language,
		title: obj(d.title),
		subtitle: obj(d.subtitle),
		theme,
		case_types: arr(d.case_types).map(c => Object.assign({
			priority: "normal", tags: [], ai_enabled: true, intro: {}, label: {}, area_id: ""
		}, c, { area_id: c && c.area_id != null ? String(c.area_id) : "" })),
		fields: arr(d.fields).map(f => Object.assign({
			help: {}, placeholder: {}, required: false, options: [], case_types: [], maps_to: "form", label: {}
		}, f)),
		description: builtin(d.description, true),
		name: builtin(d.name, false),
		email: builtin(d.email, true),
		attachments: Object.assign({}, ATTACHMENTS_DEFAULT, obj(d.attachments)),
		ai: Object.assign({ enabled: true }, obj(d.ai)),
		consent_text: obj(d.consent_text),
		privacy_url: d.privacy_url || null,
		allowed_origins: arr(d.allowed_origins),
		confirmation: { title: obj(obj(d.confirmation).title), text: obj(obj(d.confirmation).text) },
		texts: obj(d.texts),
	});
}

export function addCaseType(defn, areaId) {
	const key = makeKey("new type", defn.case_types.map(c => c.key));
	const caseType = {
		key,
		label: { [defn.default_language]: defn.default_language === "sv" ? "Ny ärendetyp" : "New case type" },
		area_id: areaId != null ? String(areaId) : "",
		priority: "normal",
		tags: [],
		ai_enabled: true,
		intro: {},
	};
	return Object.assign({}, defn, { case_types: defn.case_types.concat([caseType]) });
}

// Remove a case type, and the fields that were shown only for it. Keeps at least one.
export function removeCaseType(defn, key) {
	if (defn.case_types.length <= 1) {
		return defn;
	}
	const fields = defn.fields
		.filter(f => !(f.case_types.length === 1 && f.case_types[0] === key))
		.map(f => Object.assign({}, f, { case_types: f.case_types.filter(k => k !== key) }));
	return Object.assign({}, defn, { case_types: defn.case_types.filter(c => c.key !== key), fields });
}

export function addField(defn, type) {
	const lang = defn.default_language;
	const label = FIELD_TYPE_NAMES[type];
	const field = {
		key: makeKey(type === "email" ? "email_2" : label, defn.fields.map(f => f.key)),
		type,
		label: { [lang]: label },
		help: {},
		placeholder: {},
		required: false,
		options: type === "select" ? [{ value: "option_1", label: { [lang]: "Option 1" } }] : [],
		case_types: [],
		maps_to: "form",
	};
	return Object.assign({}, defn, { fields: defn.fields.concat([field]) });
}

export function removeField(defn, key) {
	return Object.assign({}, defn, { fields: defn.fields.filter(f => f.key !== key) });
}

// The editor tab where a save/publish problem is fixed.
export function tabForProblem(field) {
	field = field || "";
	if (field.indexOf("case_types") === 0) return TAB_TYPES;
	if (field.indexOf("fields") === 0) return TAB_FIELDS;
	if (field.indexOf("ai") === 0) return TAB_AI;
	return TAB_DESIGN;
}

// The problems in a failed save/publish. `error` is {status, body, message}
// (see api.js); the service lists them under `errors`.
export function problemsFrom(error) {
	const body = error && error.body;
	let detail;
	if (body) {
		try {
			const parsed = typeof body === "string" ? JSON.parse(body) : body;
			if (parsed && Array.isArray(parsed.errors) && parsed.errors.length) {
				return parsed.errors.map(e => ({ field: e.field || "", message: e.message || String(e) }));
			}
			if (parsed && typeof parsed.detail === "string") {
				detail = parsed.detail;
			}
		} catch (e) {
			// not JSON; fall through to the message
		}
	}
	const message = detail || (error && error.message) || I("Something went wrong.");
	return [{ field: "", message }];
}

export function statusOf(form) {
	if (!form || form.status !== "live") return "draft";
	return form.has_unpublished_changes ? "changed" : "live";
}

// Each preset sets every look setting except the brand: colour, logo, photos,
// button text and custom CSS stay as they are.
const PRESETS = {
	light: { button_text_color: null, text_color: null, background: null, field_background: null, field_border: null, page_background: null, font: "system", font_size: "normal", radius: 12, style: "flat", spacing: "normal" },
	dark: { button_text_color: null, text_color: "#e6edf3", background: "#0d1117", field_background: "#161b22", field_border: "#30363d", page_background: "#010409", font: "system", font_size: "normal", radius: 12, style: "flat", spacing: "normal" },
	minimal: { button_text_color: null, text_color: null, background: null, field_background: null, field_border: "#1f2328", page_background: null, font: "system", font_size: "normal", radius: 0, style: "none", spacing: "compact" },
	match: { button_text_color: null, text_color: "inherit", background: "transparent", field_background: null, field_border: null, page_background: null, font: "inherit", font_size: "normal", radius: 8, style: "none", spacing: "normal" },
};

export const PRESET_LIST = [
	{ key: "light", label: I("Light"), hint: I("White box, the default") },
	{ key: "dark", label: I("Dark"), hint: I("For dark websites") },
	{ key: "minimal", label: I("Minimal"), hint: I("No box, sharp lines") },
	{ key: "match", label: I("Match my website"), hint: I("Your font and colours") },
];

export function applyPreset(theme, preset) {
	return Object.assign({}, theme, PRESETS[preset] || {});
}

// Mirrors the service's check, so the problem shows while typing.
const CSS_BLOCKED = /@import|url\s*\(|image-set\s*\(|expression\s*\(|javascript:|-moz-binding|behavior\s*:|[\\<>]/i;

export function cssProblem(css) {
	css = css || "";
	if (css.length > 5000) return I("Keep it under 5,000 characters.");
	if (CSS_BLOCKED.test(css)) return I("@import, url(), expression(), < > and backslashes aren't allowed.");
	return undefined;
}

export const HEX = /^#[0-9a-fA-F]{6}$/;

// The widget's own phrases a form may replace; sv/en are the widget's
// defaults (keep in step with chat/js/cention-form.js).
export const BUILTIN_TEXTS = [
	{ key: "type", label: I("Question above the case types"), sv: "Vad gäller din fråga?", en: "What is your question about?" },
	{ key: "optional", label: I("After optional labels"), sv: "(valfritt)", en: "(optional)" },
	{ key: "attach", label: I("Attachments label"), sv: "Bilagor", en: "Attachments" },
	{ key: "drop", label: I("File drop area"), sv: "Släpp filer här eller klicka för att välja", en: "Drop files here or click to choose" },
	{ key: "remove", label: I("Remove a file"), sv: "Ta bort", en: "Remove" },
	{ key: "looking", label: I("While the AI looks"), sv: "Letar efter ett svar…", en: "Looking for an answer…" },
	{ key: "ai", label: I("Above the AI answer"), sv: "Sammanfattat av AI", en: "Summarized by AI" },
	{ key: "sources", label: I("Above the sources"), sv: "Källor", en: "Sources" },
	{ key: "resolved", label: I("“Solved” button"), sv: "Det är löst, tack!", en: "It's been resolved, thank you!" },
	{ key: "thanks", label: I("After “solved”"), sv: "Vad bra att vi kunde hjälpa till!", en: "Great, glad we could help!" },
	{ key: "still", label: I("Send anyway link"), sv: "Jag vill ändå skicka min fråga", en: "I still want to send my question" },
	{ key: "sending", label: I("Send button while sending"), sv: "Skickar…", en: "Sending…" },
	{ key: "required", label: I("Missing required field"), sv: "Fältet måste fyllas i.", en: "This field is required." },
	{ key: "email", label: I("Invalid email"), sv: "Ange en giltig e-postadress.", en: "Enter a valid email address." },
	{ key: "option", label: I("No option chosen"), sv: "Välj ett av alternativen.", en: "Choose one of the options." },
	{ key: "upFailed", label: I("Upload failed"), sv: "Filen kunde inte laddas upp.", en: "The file couldn't be uploaded." },
	{ key: "tooMany", label: I("Too many files"), sv: "Du kan bifoga högst 5 filer.", en: "You can attach up to 5 files." },
	{ key: "failed", label: I("Sending failed"), sv: "Vi kunde inte skicka ditt meddelande. Försök igen.", en: "We couldn't send your message. Please try again." },
	{ key: "rate", label: I("Too many attempts"), sv: "För många försök. Vänta en stund och försök igen.", en: "Too many attempts. Please wait a moment and try again." },
	{ key: "privacy", label: I("Privacy policy link"), sv: "Integritetspolicy", en: "Privacy policy" },
	{ key: "unavailable", label: I("Form not available"), sv: "Formuläret är inte tillgängligt.", en: "This form isn't available." },
];

// Set or clear one built-in phrase in one language; empty phrases are removed.
export function setBuiltinText(defn, key, lang, value) {
	const texts = Object.assign({}, defn.texts || {});
	const entry = Object.assign({}, texts[key] || {});
	if (value && value.trim()) {
		entry[lang] = value;
	} else {
		delete entry[lang];
	}
	if (Object.keys(entry).length) {
		texts[key] = entry;
	} else {
		delete texts[key];
	}
	return Object.assign({}, defn, { texts });
}

// The problem message for a path, e.g. problemFor(problems, "case_types[1].area_id").
// A problem on an item of a list (e.g. "allowed_origins[2]") shows at the list.
export function problemFor(problems, path) {
	const list = problems || [];
	const p = list.find(x => x.field === path)
		|| list.find(x => x.field && x.field.indexOf(path + "[") === 0);
	return p ? p.message : undefined;
}

// Comma list -> tags (trimmed, non-empty, at most 10).
export function parseTags(value) {
	return (value || "").split(",").map(t => t.trim()).filter(Boolean).slice(0, 10);
}

// One address per line -> allowed origins (no trailing slash, at most 20).
export function parseOrigins(value) {
	return (value || "").split("\n").map(s => s.trim().replace(/\/+$/, "")).filter(Boolean).slice(0, 20);
}

// One https address per line -> team photos (at most 3).
export function parseAvatars(value) {
	return (value || "").split("\n").map(s => s.trim()).filter(Boolean).slice(0, 3);
}

// The hosted page URL with a preview token and language.
export function previewUrl(link, token, lang, nonce) {
	if (!link || !token) {
		return null;
	}
	const sep = link.indexOf("?") >= 0 ? "&" : "?";
	return link + sep + "preview=" + encodeURIComponent(token)
		+ "&lang=" + encodeURIComponent(lang || "")
		+ (nonce ? "&n=" + nonce : "");
}
